import { useEffect, useState, useRef } from "react";
import useAuth from "../../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../../config";
import {
  setRichiesteFilters,
  clearRichiesteFilters,
} from "../../../redux/slices/richiesteFilters";
import { useFilterNavigation } from "../../../hooks/useFilterNavigation";
import {
  setPageNumber,
  setRowsPerPage,
  resetPagination,
} from "../../../redux/slices/richiestePagination";
import { useDispatch, useSelector } from "react-redux";

export const Data = () => {
  const dispatch = useDispatch();
  const filterData = useSelector((state) => state.richiesteFilters);
  const { pageNumber, rowsPerPage } = useSelector(
    (state) => state.richiestePagination,
  );

  // Temp state for filters (the values currently in the input fields)
  const [tempFilterData, setTempFilterData] = useState(filterData || {});

  const [filterOptions, setFilterOptions] = useState(null);
  const [filterByDate, setFilterByDate] = useState(null);
  const [dateFilterNumber, setDateFilterNumber] = useState(30);
  const [doughnutChartData, setDoughnutChartData] = useState(null);
  const [barChartData, setBarChartData] = useState(null);
  const [richiesteData, setRichiesteData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isTableChanging, setIsTableChanging] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [isRemovingFilters, setIsRemovingFilters] = useState(false);
  const [canClearFilters, setCanClearFilters] = useState(false);
  const [isApplyFiltersButtonDisabled, setIsApplyFiltersButtonDisabled] =
    useState(true);
  const idTipologia = 8;

  const navigate = useNavigate();
  const pollingIntervalRef = useRef(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());

  // Sync temp state when Redux filters are restored or cleared
  useEffect(() => {
    setTempFilterData(filterData || {});
  }, [filterData]);

  // Use filter navigation hook to manage persistence based on navigation source
  useFilterNavigation(
    () => {
      // onRestoreFilters: Keep filters from Redux (they're already loaded)
      if (filterData) {
        setCanClearFilters(true);
      }
    },
    () => {
      // onClearFilters: Clear filters if not coming from details page
      dispatch(clearRichiesteFilters());
      setCanClearFilters(false);
    },
  );

  async function richiesteGetList(rowsPages, pageNumbers, filters) {
    const token = sessionStorage.getItem("token");
    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          rowsPages,
          pageNumbers,
          ...filters,
        }),
      };

      const response = await fetch(
        `${API_BASE_URL}/api/requestGetList`,
        requestOptions,
      );
      const responseData = await response.json();

      if (responseData.status === -1) {
        sessionStorage.clear();
        navigate("/auth/sign-in");
      }

      return responseData;
    } catch (error) {
      return error;
    }
  }
  async function richiesteGetFilters() {
    const token = sessionStorage.getItem("token");
    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + token,
        },
      };

      const response = await fetch(
        `${API_BASE_URL}/api/requestGetFilter`,
        requestOptions,
      );
      const responseData = await response.json();

      if (responseData.status === -1) {
        sessionStorage.clear();
        navigate("/auth/sign-in");
      }

      return responseData;
    } catch (error) {
      return error;
    }
  }

  // Side Effect that handles the get rc list api request initially when the page loads and then when the page number or rows change
  useEffect(() => {
    setIsTableChanging(true);
    richiesteGetList(
      rowsPerPage,
      pageNumber,
      filterByDate ? { ...filterData, aging: filterByDate } : filterData,
    )
      .then((response) => {
        if (response.status === 1) {
          setDoughnutChartData(response?.data?.graphCircleData);
          setBarChartData(response?.data?.graphBarData);
          setTotalRows(response?.data?.totalRows);
          setRichiesteData(response?.data?.dataset);
          setLastUpdateTime(new Date());
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
        setIsTableChanging(false);
        setIsRemovingFilters(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, rowsPerPage, filterData, filterByDate]); // Trigger on applied filters and aging change

  // Side Effect that get filters data api which runs only once when the page loads and in response sets the filters.
  useEffect(() => {
    richiesteGetFilters().then((response) => {
      if (response.status === 1) {
        setFilterOptions(response.data);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Side Effect that checks if the user has set filters and in truly it enables the Search button
  useEffect(() => {
    const hasActiveFilters = (data) => {
      if (!data) return false;
      return Object.values(data).some(
        (v) => v !== "" && v !== null && v !== undefined,
      );
    };

    setIsApplyFiltersButtonDisabled(!hasActiveFilters(tempFilterData));
    setCanClearFilters(hasActiveFilters(filterData));
  }, [tempFilterData, filterData]);

  // Polling effect - refresh data every 30 seconds
  useEffect(() => {
    let timeoutId;

    const poll = async () => {
      // Only poll if not currently loading or changing filters
      if (
        !loading &&
        !isTableChanging &&
        !filtersLoading &&
        !isRemovingFilters
      ) {
        try {
          const response = await richiesteGetList(
            rowsPerPage,
            pageNumber,
            filterByDate ? { ...filterData, aging: filterByDate } : filterData,
          );
          if (response.status === 1) {
            setDoughnutChartData(response?.data?.graphCircleData);
            setBarChartData(response?.data?.graphBarData);
            setTotalRows(response?.data?.totalRows);
            setRichiesteData(response?.data?.dataset);
            setLastUpdateTime(new Date());
          }
        } catch (err) {
          console.log("Polling error:", err);
        }
      }
      // Schedule next poll after 30 seconds
      timeoutId = setTimeout(poll, 30000);
    };

    // Start polling when component mounts and data is initially loaded
    if (!loading) {
      timeoutId = setTimeout(poll, 30000);
    }

    // Cleanup polling on unmount or dependency change
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    loading,
    isTableChanging,
    filtersLoading,
    isRemovingFilters,
    rowsPerPage,
    pageNumber,
    filterData,
    filterByDate,
  ]);

  // function that changes the table page
  const handlePageChange = (newPage) => {
    if (isTableChanging) {
      return;
    } else {
      dispatch(setPageNumber(newPage + 1));
    }
  };

  // function that changes the table rows (size)
  const handlePageSizeChange = (newPageSize) => {
    dispatch(setRowsPerPage(newPageSize));
    dispatch(setPageNumber(1));
  };

  const handleDateFilterChange = (value) => {
    setFilterByDate(value);

    if (pageNumber !== 1) {
      dispatch(setPageNumber(1));
    }
  };

  // function that handles the get rc list api call with the filter the user has set and in response updates the list
  const handleApplyFilters = () => {
    setFiltersLoading(true);
    setIsTableChanging(true);
    setFilterByDate(null);

    // Save current temp filters to Redux
    dispatch(setRichiesteFilters(tempFilterData));
    dispatch(setPageNumber(1)); // Reset to first page on search

    setFiltersLoading(false);
  };

  const handleSaveFilters = () => {
    dispatch(setRichiesteFilters(tempFilterData));
  };

  // function that handles the get rc list api call when the user removes all filters and in response the list is updated with the initial state
  const handleRemoveFilters = () => {
    const shouldReload =
      filterData !== null || filterByDate !== null || pageNumber !== 1;

    if (shouldReload) {
      setIsTableChanging(true);
      setIsRemovingFilters(true);
    }

    dispatch(clearRichiesteFilters());
    dispatch(setPageNumber(1));

    // Reset local/temp state as well
    setTempFilterData({});
    setCanClearFilters(false);
    setFilterByDate(null);
    setDateFilterNumber(30);
  };

  return {
    handlePageChange,
    handlePageSizeChange,
    handleApplyFilters,
    handleRemoveFilters,
    handleSaveFilters,
    doughnutChartData,
    barChartData,
    richiesteData,
    totalRows,
    loading,
    filtersLoading,
    isApplyFiltersButtonDisabled,
    pageNumber,
    rowsPerPage,
    filterOptions,
    filterData: tempFilterData, // Expose temp filters to the UI components
    setFilterData: setTempFilterData, // Update temp filters on input change
    setFilterByDate: handleDateFilterChange,
    canClearFilters,
    isRemovingFilters,
    dateFilterNumber,
    setDateFilterNumber,
    lastUpdateTime,
  };
};
